import { InspectionFilters, InspectionResultStatus } from '@/types/model/inspection';
import { PageHeaderFilterControl, PageHeaderFilterOption } from '@/types/model/pageHeaderBar';

const ALL_STATUSES: InspectionResultStatus[] = [
  'unknown',
  'not-started',
  'in-progress',
  'approved',
  'disapproved',
  'completed',
];

const truncate = (value: string, maxLength: number = 28): string => {
  if (value.length <= maxLength) {
    return value;
  }

  return `${value.slice(0, Math.max(0, maxLength - 3))}...`;
};

const formatStatuses = (statuses: InspectionResultStatus[]): string => {
  if (statuses.length === 0) {
    return 'None';
  }

  return truncate(statuses.join(', '));
};

export const buildInspectingFilterControls = (
  filters: InspectionFilters
): PageHeaderFilterControl<InspectionResultStatus>[] => {
  const statusOptions: PageHeaderFilterOption<InspectionResultStatus>[] = ALL_STATUSES.map((status) => ({
    label: status,
    value: status,
  }));

  return [
    {
      type: 'toggle',
      id: 'inspecting-show-all-filter',
      label: 'Show All',
      checked: filters.showAll,
    },
    {
      type: 'select',
      id: 'inspecting-status-filter',
      label: 'Statuses',
      value: filters.selectedStatuses,
      options: statusOptions,
      multiple: true,
      placeholder: 'Select statuses',
      selectedText: formatStatuses(filters.selectedStatuses),
      disabledWhen: {
        controlId: 'inspecting-show-all-filter',
        checked: true,
      },
    },
  ];
};

const getToggleValue = (
  controls: PageHeaderFilterControl<InspectionResultStatus>[],
  controlId: string
): boolean | null => {
  const control = controls.find((candidate) => candidate.id === controlId);
  if (!control || control.type !== 'toggle') {
    return null;
  }

  return control.checked;
};

const getSelectedValues = (
  controls: PageHeaderFilterControl<InspectionResultStatus>[],
  controlId: string
): InspectionResultStatus[] => {
  const control = controls.find((candidate) => candidate.id === controlId);
  if (!control || control.type !== 'select') {
    return [];
  }

  return Array.isArray(control.value) ? control.value : [control.value];
};

export const mapInspectingControlsToFilters = (
  controls: PageHeaderFilterControl<InspectionResultStatus>[],
  currentFilters: InspectionFilters
): InspectionFilters => {
  const showAll = getToggleValue(controls, 'inspecting-show-all-filter') ?? currentFilters.showAll;

  if (showAll) {
    return {
      ...currentFilters,
      showAll: true,
      selectedStatuses: [...ALL_STATUSES],
    };
  }

  return {
    ...currentFilters,
    showAll: false,
    selectedStatuses: getSelectedValues(controls, 'inspecting-status-filter'),
  };
};

export const buildInspectingFilterSummary = (filters: InspectionFilters): string => {
  if (filters.showAll) {
    return 'All';
  }

  return `Statuses: ${formatStatuses(filters.selectedStatuses)}`;
};

export const buildInspectingActionMessage = (
  isCaching: boolean,
  progress: number,
  hasCachedInspections: boolean
): string => {
  if (isCaching) {
    return `Downloading in-progress inspections ${Math.round(progress)}%`;
  }

  if (hasCachedInspections) {
    return 'Cached data exists. Clear it in Offline Operations before downloading again.';
  }

  return 'Download in-progress inspections for offline use.';
};
